import React from 'react';
import { Grid } from 'antd';
import { Card, Typography, Tooltip } from 'antd';
import { filterByNits, aggregateByDate } from '../../utils/inventoryReports';

const { useBreakpoint } = Grid;
const { Text } = Typography;

/**
 * InventoryHealthGauge - Professional gauge showing overall inventory health
 * @param {Object} props
 * @param {Array} props.data - Array of DailyInventoryDistributionRecord
 * @param {string[]} props.nitList - Selected NITs for filtering
 */
function InventoryHealthGauge({ data, nitList }) {
  const screens = useBreakpoint();
  const isXs = !screens.xs;

  // Calculate health score based on latest record
  const healthData = React.useMemo(() => {
    if (!data || data.length === 0) return null;

    // Filtra por los NITs seleccionados (uno o varios) y consolida por fecha
    // para que la salud refleje el inventario agregado de toda la selección.
    const filtered = filterByNits(data, nitList);
    if (filtered.length === 0) return null;

    const aggregated = aggregateByDate(filtered);
    if (aggregated.length === 0) return null;

    // Último día consolidado (aggregateByDate ordena ascendentemente)
    const latestRecord = aggregated[aggregated.length - 1];

    const total = latestRecord.TotalReferences || 0;
    const azulCount = latestRecord.AzulCount || 0;
    const verdeCount = latestRecord.VerdeCount || 0;
    const amarilloCount = latestRecord.AmarilloCount || 0;
    const rojoCount = latestRecord.RojoCount || 0;
    const negroCount = latestRecord.NegroCount || 0;

    // Health score: weighted calculation
    // Verde (100pts), Amarillo (75pts), Azul (50pts - sobre-inventario), Rojo (25pts), Negro (0pts)
    const weightedScore = (
      (verdeCount * 100) +
      (amarilloCount * 75) +
      (azulCount * 50) +
      (rojoCount * 25) +
      (negroCount * 0)
    ) / (total || 1);

    const healthPercent = Math.min(100, Math.max(0, weightedScore));

    return {
      date: latestRecord.RecordDate,
      healthPercent,
      total,
      azulCount,
      verdeCount,
      amarilloCount,
      rojoCount,
      negroCount,
      healthyCount: verdeCount + amarilloCount,
      warningCount: amarilloCount,
      criticalCount: rojoCount + negroCount
    };
  }, [data, nitList]);

  const getHealthConfig = (percent) => {
    if (percent >= 80) {
      return {
        color: '#059669',
        gradientFrom: '#059669',
        gradientTo: '#34d399',
        label: 'Excelente',
        bgColor: '#ecfdf5',
        emoji: '🟢',
        description: 'Inventario óptimo',
        dotColor: '#10b981'
      };
    }
    if (percent >= 60) {
      return {
        color: '#16a34a',
        gradientFrom: '#16a34a',
        gradientTo: '#4ade80',
        label: 'Saludable',
        bgColor: '#dcfce7',
        emoji: '🟢',
        description: 'Buen estado general',
        dotColor: '#22c55e'
      };
    }
    if (percent >= 40) {
      return {
        color: '#ca8a04',
        gradientFrom: '#ca8a04',
        gradientTo: '#facc15',
        label: 'Atención',
        bgColor: '#fefce8',
        emoji: '🟡',
        description: 'Requiere monitoreo',
        dotColor: '#eab308'
      };
    }
    if (percent >= 20) {
      return {
        color: '#ea580c',
        gradientFrom: '#ea580c',
        gradientTo: '#fb923c',
        label: 'Preocupante',
        bgColor: '#ffedd5',
        emoji: '🟠',
        description: 'Acción necesaria',
        dotColor: '#f97316'
      };
    }
    return {
      color: '#dc2626',
      gradientFrom: '#dc2626',
      gradientTo: '#f87171',
      label: 'Crítico',
      bgColor: '#fee2e2',
      emoji: '🔴',
      description: 'Atención inmediata',
      dotColor: '#ef4444'
    };
  };

  // SVG Circular Gauge Component
  const CircularGauge = ({ percent, config, size }) => {
    const strokeWidth = size * 0.12;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percent / 100) * circumference;

    return (
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#f3f4f6"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#gradient-${config.color})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: 'stroke-dashoffset 1s ease-in-out',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
          }}
        />
        {/* Gradient definition */}
        <defs>
          <linearGradient id={`gradient-${config.color}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={config.gradientFrom} />
            <stop offset="100%" stopColor={config.gradientTo} />
          </linearGradient>
        </defs>
      </svg>
    );
  };

  if (!healthData) {
    return (
      <Card
        bordered={false}
        className="shadow-sm"
        style={{
          height: '100%',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 10px 40px rgba(102, 126, 234, 0.15)'
        }}
      >
        <div style={{ textAlign: 'center', padding: isXs ? '30px 20px' : '50px 30px', color: 'rgba(255,255,255,0.9)' }}>
          <div style={{ fontSize: 56, marginBottom: 16, opacity: 0.8 }}>📊</div>
          <div style={{ fontSize: isXs ? 16 : 18, fontWeight: 500 }}>Sin datos disponibles</div>
          <div style={{ fontSize: isXs ? 12 : 14, marginTop: 8, opacity: 0.7 }}>
            Selecciona un rango de fechas para ver el análisis
          </div>
        </div>
      </Card>
    );
  }

  const healthConfig = getHealthConfig(healthData.healthPercent);
  const gaugeSize = isXs ? 140 : 180;

  return (
    <Card
      bordered={false}
      className="shadow-sm"
      style={{
        height: '100%',
        borderRadius: '16px',
        background: '#ffffff',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0'
      }}
      bodyStyle={{ padding: isXs ? 20 : 24 }}
    >
      {/* Header */}
      <div style={{ marginBottom: isXs ? 20 : 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <Text style={{ fontSize: isXs ? 16 : 18, fontWeight: 600, color: '#1f2937' }}>
            Salud del Inventario
          </Text>
          <div style={{
            padding: '4px 12px',
            borderRadius: '20px',
            backgroundColor: healthConfig.bgColor,
            fontSize: isXs ? 11 : 12,
            fontWeight: 600,
            color: healthConfig.color,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6
          }}>
            <span style={{ fontSize: 14 }}>{healthConfig.emoji}</span>
            {healthConfig.label}
          </div>
        </div>
        <Text type="secondary" style={{ fontSize: isXs ? 12 : 13 }}>
          {healthConfig.description} • Actualizado: {healthData.date}
        </Text>
      </div>

      {/* Main Gauge */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: isXs ? 24 : 28, position: 'relative' }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <CircularGauge
            percent={healthData.healthPercent}
            config={healthConfig}
            size={gaugeSize}
          />
          {/* Center content */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: isXs ? 32 : 44,
              fontWeight: 700,
              color: healthConfig.color,
              lineHeight: 1,
              fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif"
            }}>
              {Math.round(healthData.healthPercent)}
            </div>
            <div style={{
              fontSize: isXs ? 11 : 13,
              color: '#6b7280',
              marginTop: 4,
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Salud
            </div>
          </div>
        </div>
      </div>

      {/* Breakdown Stats */}
      <div style={{
        backgroundColor: '#fafafa',
        borderRadius: '12px',
        padding: isXs ? 16 : 20,
        marginBottom: 16
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: isXs ? 8 : 12,
          textAlign: 'center'
        }}>
          {/* Azul */}
          <Tooltip title="Exceso de inventario (>100%) - Posible sobre-inversión">
          <div>
            <div style={{
              width: 12,
              height: 12,
              backgroundColor: '#2563eb',
              borderRadius: '50%',
              margin: '0 auto 8px',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)'
            }} />
            <div style={{
              fontSize: isXs ? 16 : 20,
              fontWeight: 700,
              color: '#1e40af',
              lineHeight: 1.2
            }}>
              {healthData.azulCount}
            </div>
            <div style={{
              fontSize: isXs ? 10 : 11,
              color: '#6b7280',
              marginTop: 2,
              fontWeight: 500
            }}>
              Azul
            </div>
          </div>
          </Tooltip>

          {/* Verde */}
          <Tooltip title="Stock adecuado (70-100%) - Todo bien">
          <div>
            <div style={{
              width: 12,
              height: 12,
              backgroundColor: '#16a34a',
              borderRadius: '50%',
              margin: '0 auto 8px',
              boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)'
            }} />
            <div style={{
              fontSize: isXs ? 16 : 20,
              fontWeight: 700,
              color: '#166534',
              lineHeight: 1.2
            }}>
              {healthData.verdeCount}
            </div>
            <div style={{
              fontSize: isXs ? 10 : 11,
              color: '#6b7280',
              marginTop: 2,
              fontWeight: 500
            }}>
              Verde
            </div>
          </div>
          </Tooltip>

          {/* Amarillo */}
          <Tooltip title="Stock moderado (30-70%) - Monitorear">
          <div>
            <div style={{
              width: 12,
              height: 12,
              backgroundColor: '#ca8a04',
              borderRadius: '50%',
              margin: '0 auto 8px',
              boxShadow: '0 2px 8px rgba(202, 138, 4, 0.3)'
            }} />
            <div style={{
              fontSize: isXs ? 16 : 20,
              fontWeight: 700,
              color: '#854d0e',
              lineHeight: 1.2
            }}>
              {healthData.amarilloCount}
            </div>
            <div style={{
              fontSize: isXs ? 10 : 11,
              color: '#6b7280',
              marginTop: 2,
              fontWeight: 500
            }}>
              Amarillo
            </div>
          </div>
          </Tooltip>

          {/* Rojo */}
          <Tooltip title="Stock muy bajo (10-30%) - Pedir pronto">
          <div>
            <div style={{
              width: 12,
              height: 12,
              backgroundColor: '#dc2626',
              borderRadius: '50%',
              margin: '0 auto 8px',
              boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
            }} />
            <div style={{
              fontSize: isXs ? 16 : 20,
              fontWeight: 700,
              color: '#991b1b',
              lineHeight: 1.2
            }}>
              {healthData.rojoCount}
            </div>
            <div style={{
              fontSize: isXs ? 10 : 11,
              color: '#6b7280',
              marginTop: 2,
              fontWeight: 500
            }}>
              Rojo
            </div>
          </div>
          </Tooltip>

          {/* Negro */}
          <Tooltip title="Sin stock prácticamente (<10%) - Pedido urgente">
          <div>
            <div style={{
              width: 12,
              height: 12,
              backgroundColor: '#1f2937',
              borderRadius: '50%',
              margin: '0 auto 8px',
              boxShadow: '0 2px 8px rgba(31, 41, 55, 0.3)'
            }} />
            <div style={{
              fontSize: isXs ? 16 : 20,
              fontWeight: 700,
              color: '#111827',
              lineHeight: 1.2
            }}>
              {healthData.negroCount}
            </div>
            <div style={{
              fontSize: isXs ? 10 : 11,
              color: '#6b7280',
              marginTop: 2,
              fontWeight: 500
            }}>
              Negro
            </div>
          </div>
          </Tooltip>
        </div>

        {/* Total */}
        <div style={{
          marginTop: 16,
          paddingTop: 12,
          borderTop: '1px dashed #e5e7eb',
          textAlign: 'center',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 8
        }}>
          <Text style={{ fontSize: isXs ? 13 : 14, color: '#6b7280' }}>
            Total de SKUs analizados:
          </Text>
          <Text strong style={{ fontSize: isXs ? 16 : 18, color: '#1f2937' }}>
            {healthData.total}
          </Text>
        </div>
      </div>

      {/* Status Indicator Bar */}
      <div style={{
        marginTop: 16,
        padding: '12px 16px',
        backgroundColor: healthConfig.bgColor,
        borderRadius: '10px',
        borderLeft: `4px solid ${healthConfig.color}`,
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <span style={{ fontSize: 20 }}>{healthConfig.emoji}</span>
        <div style={{ flex: 1 }}>
          <Text style={{ fontSize: isXs ? 13 : 14, fontWeight: 600, color: healthConfig.color }}>
            Estado: {healthConfig.label}
          </Text>
          <Text type="secondary" style={{ fontSize: isXs ? 11 : 12, display: 'block', marginTop: 2 }}>
            {healthData.healthyCount} SKUs en nivel óptimo • {healthData.criticalCount} requieren atención
          </Text>
        </div>
      </div>
    </Card>
  );
}

export default InventoryHealthGauge;
